(() => {
  const ready = (fn) => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn();

  ready(() => {
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = document.querySelector('[data-menu-drawer]');
        if (!drawer) return;
        const open = drawer.classList.toggle('is-open');
        document.body.classList.toggle('menu-open', open);
        button.setAttribute('aria-expanded', String(open));
      });
    });

    document.querySelectorAll('[data-menu-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = document.querySelector('[data-menu-drawer]');
        if (!drawer) return;
        drawer.classList.remove('is-open');
        document.body.classList.remove('menu-open');
        document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') document.querySelector('[data-menu-close]')?.click();
    });

    const initHeroSlider = (slider) => {
      if (!slider || slider.dataset.sliderReady === 'true') return;
      const slides = Array.from(slider.querySelectorAll('[data-slide]'));
      const dots = Array.from(slider.querySelectorAll('[data-slide-dot]'));
      if (!slides.length) return;

      slider.dataset.sliderReady = 'true';
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let pointerStartX = null;
      let pointerStartY = null;
      let pausedByHover = false;
      const rawDelay = Number(slider.dataset.autoplayDelay || 4000);
      const delay = Number.isFinite(rawDelay) ? Math.max(1500, rawDelay) : 4000;
      const autoplay = slider.dataset.autoplay === 'true' && slides.length > 1;
      const progress = slider.querySelector('[data-slider-progress]');

      const restartProgress = () => {
        if (!progress) return;
        progress.style.animation = 'none';
        progress.offsetHeight;
        if (autoplay && !document.hidden && !pausedByHover) {
          progress.style.animation = `neuHeroProgress ${delay}ms linear forwards`;
        }
      };

      const show = (next, userInitiated = false) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          if ('inert' in slide) slide.inert = !active;
        });
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
        slider.dataset.activeSlide = String(index);
        restartProgress();
        if (userInitiated) restart();
      };

      const stop = () => {
        if (timer) window.clearTimeout(timer);
        timer = null;
        if (progress) progress.style.animationPlayState = 'paused';
      };

      const restart = () => {
        stop();
        if (!autoplay || document.hidden || pausedByHover) return;
        restartProgress();
        timer = window.setTimeout(() => {
          show(index + 1);
          restart();
        }, delay);
      };

      slider.querySelector('[data-slide-prev]')?.addEventListener('click', () => show(index - 1, true));
      slider.querySelector('[data-slide-next]')?.addEventListener('click', () => show(index + 1, true));
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i, true)));

      if (window.matchMedia?.('(hover: hover)').matches) {
        slider.addEventListener('mouseenter', () => { pausedByHover = true; stop(); });
        slider.addEventListener('mouseleave', () => { pausedByHover = false; restart(); });
      }

      const beginSwipe = (x, y) => {
        pointerStartX = x;
        pointerStartY = y;
        slider.classList.add('is-touching');
      };
      const endSwipe = (x, y) => {
        if (pointerStartX === null || pointerStartY === null) return;
        const dx = x - pointerStartX;
        const dy = y - pointerStartY;
        pointerStartX = null;
        pointerStartY = null;
        slider.classList.remove('is-touching');
        if (Math.abs(dx) >= 42 && Math.abs(dx) > Math.abs(dy) * 1.15) {
          show(index + (dx < 0 ? 1 : -1), true);
        }
      };

      if ('PointerEvent' in window) {
        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          beginSwipe(event.clientX, event.clientY);
        }, { passive: true });
        slider.addEventListener('pointerup', (event) => endSwipe(event.clientX, event.clientY), { passive: true });
        slider.addEventListener('pointercancel', () => {
          pointerStartX = null;
          pointerStartY = null;
          slider.classList.remove('is-touching');
        }, { passive: true });
      } else {
        slider.addEventListener('touchstart', (event) => beginSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY), { passive: true });
        slider.addEventListener('touchend', (event) => endSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY), { passive: true });
      }

      const onVisibility = () => document.hidden ? stop() : restart();
      document.addEventListener('visibilitychange', onVisibility);

      slider._neuHeroShow = show;
      slider._neuHeroRestart = restart;
      show(index);
      restart();
    };

    document.querySelectorAll('[data-slider]').forEach(initHeroSlider);

    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll?.('[data-slider]').forEach(initHeroSlider);
      if (event.target.matches?.('[data-slider]')) initHeroSlider(event.target);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const slider = event.target.closest?.('[data-slider]');
      if (!slider || typeof slider._neuHeroShow !== 'function') return;
      const slide = event.target.closest?.('[data-slide]');
      const slides = Array.from(slider.querySelectorAll('[data-slide]'));
      const selectedIndex = slide ? slides.indexOf(slide) : -1;
      if (selectedIndex >= 0) slider._neuHeroShow(selectedIndex, true);
    });

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealTargets = Array.from(document.querySelectorAll('.neu-section .section-heading, .section-card, .portal-card, .product-card, .guide-card, .result-card, .testimonial-card, .academy-card, .fame-card, .country-item, .faq-item, .community-post, .final-cta, .store-hero, .store-product-card, .blog-card, .community-portal, .students-hero__content, .students-featured .students-carousel, .students-directory__header, .student-card, .students-testimonials__grid > *, .students-inline-cta__card, .trainers-hero__content, .trainers-featured .students-carousel, .trainer-card, .resources-hero__content, .resources-tools, .resource-card, .product-gallery, .product-info'));
    if (revealTargets.length) {
      document.documentElement.classList.add('neu-motion-ready');
      revealTargets.forEach((element, index) => {
        element.setAttribute('data-neu-reveal', '');
        element.style.setProperty('--neu-reveal-delay', `${(index % 4) * 65}ms`);
      });
      if (reduceMotion || !('IntersectionObserver' in window)) {
        revealTargets.forEach((element) => element.classList.add('is-visible'));
      } else {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        }, { threshold: .12, rootMargin: '0px 0px -5% 0px' });
        revealTargets.forEach((element) => observer.observe(element));
      }
    }

    document.querySelectorAll('[data-premium-link]').forEach((link) => {
      if (link.dataset.premiumLinkReady === 'true') return;
      link.dataset.premiumLinkReady = 'true';
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const href = link.getAttribute('href');
        if (!href || href === '#' || link.target === '_blank' || reduceMotion) return;
        event.preventDefault();
        link.classList.add('is-activating');
        window.setTimeout(() => { window.location.href = href; }, 220);
      });
    });
  });
})();
